import { useEffect, useMemo, useState } from "react";

interface UsePaginationStateResult<T> {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  rowsPerPage: number;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  paginatedData: T[];
  totalPages: number;
  rangeLabel: string;
  handlePageChange: (_: React.ChangeEvent<unknown>, value: number) => void;
  handleRowsPerPageChange: (value: number) => void;
}

/**
 * Centraliza o estado de paginação no cliente.
 * Reseta automaticamente para a página 1 quando `data` muda de referência.
 */
export function usePaginationState<T>(
  data: T[],
  defaultRowsPerPage = 10
): UsePaginationStateResult<T> {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  useEffect(() => {
    setPage(1);
  }, [data]);

  const totalPages = Math.max(1, Math.ceil(data.length / rowsPerPage));

  const paginatedData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return data.slice(start, start + rowsPerPage);
  }, [data, page, rowsPerPage]);

  const from = data.length === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const to = Math.min(page * rowsPerPage, data.length);
  const rangeLabel = `${from}-${to} de ${data.length}`;

  function handlePageChange(_: React.ChangeEvent<unknown>, value: number) {
    setPage(value);
  }

  function handleRowsPerPageChange(value: number) {
    setRowsPerPage(value);
    setPage(1);
  }

  return {
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    paginatedData,
    totalPages,
    rangeLabel,
    handlePageChange,
    handleRowsPerPageChange,
  };
}
